import type { FC } from "react";
import { useCallback } from "react";

import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";

import { useTranslation } from "react-i18next";

import type { BaseCreateType } from "shared/types";

import { DividerWithText, Input, Modal, Text, Uploader } from "shared/uikit";
import { ModalSizesEnum } from "shared/uikit/BaseDialog";
import { TextVariantsEnum } from "shared/uikit/types";
import { returnDirtyFieldsOnly } from "utils/returnDirtyFieldsOnly";
import { phoneValidator, validateURL } from "utils/serviceUtils/validators";

import FormBlock from "../FormBlock";
import LocationInput from "../LocationInput";

type FormData = Omit<BaseCreateType, "location"> & { location: { value: string; label: string } };

export interface EntitySettingsTemplateModalProps {
	title: string;
	onClose: () => void;
}

const EntitySettingsTemplateModal: FC<
	EntitySettingsTemplateModalProps & {
		data: BaseCreateType;
		entityName: string;
		handleUpdateEntity: (data: Partial<BaseCreateType>) => void;
		thirdColumnContent?: React.ReactNode;
		submitting?: boolean;
	}
> = ({ title, entityName, onClose, data, handleUpdateEntity, thirdColumnContent, submitting }) => {
	const { t } = useTranslation();

	const { handleSubmit, formState, control } = useForm<FormData>({
		mode: "onChange"
	});

	const { isDirty, errors, dirtyFields } = formState;

	const onSubmit: SubmitHandler<FormData> = useCallback(
		data => {
			const latLng = data.location.value.split("lng");
			const dirtyData = returnDirtyFieldsOnly(dirtyFields, data);

			const location = dirtyData.location
				? {
						address: data.location.label,
						lat: Number(latLng[0]),
						lng: Number(latLng[1])
					}
				: undefined;

			handleUpdateEntity({
				...dirtyData,
				location
			});
		},
		[dirtyFields, handleUpdateEntity]
	);

	return (
		<Modal
			open
			onClose={onClose}
			sizes={ModalSizesEnum.FULLSCREEN}
			title={title}
			actions={[
				{
					text: t("save_changes"),
					onClick: handleSubmit(onSubmit),
					disabled: !isDirty || submitting,
					loading: submitting
				}
			]}
		>
			<div className="p-6 flex gap-2 justify-between flex-col lg:gap-6 lg:flex-row">
				<div className="mb-3 flex flex-col gap-4 flex-1">
					<Text variants={TextVariantsEnum.H7}>
						{entityName} {t("details").toLowerCase()}
					</Text>
					<FormBlock label={`${entityName} logo`} textVariant={TextVariantsEnum.Caption2} labelClassname="mb-2">
						<Controller
							name="logo"
							control={control}
							defaultValue={data?.logo}
							render={({ field: { value, onChange } }) => <Uploader src={value} onChange={onChange} />}
						/>
					</FormBlock>
					<div className="flex flex-col gap-4 mt-2">
						<Controller
							name="name"
							control={control}
							defaultValue={data.name}
							render={({ field: { value, onChange } }) => (
								<FormBlock label={`${entityName} name`} textVariant={TextVariantsEnum.Caption2} labelClassname="mb-2">
									<Input onChange={onChange} value={value} placeholder={`${entityName} ${t("name").toLowerCase()}`} />
								</FormBlock>
							)}
						/>

						<FormBlock label={t("abbreviated_name")} textVariant={TextVariantsEnum.Caption2} labelClassname="mb-2">
							<Input onChange={() => {}} value="" disabled placeholder={t("abbreviated_name")} />
						</FormBlock>

						<Controller
							name="location"
							control={control}
							defaultValue={
								data.location
									? {
											label: data.location?.address || "",
											value: `${data.location.lat}lng${data.location.lng}`
										}
									: undefined
							}
							render={({ field: { onChange, value } }) => (
								<FormBlock
									label={`${entityName} ${t("address")}`}
									textVariant={TextVariantsEnum.Caption2}
									labelClassname="mb-2"
									containerClassName="mb-4"
								>
									<LocationInput onChange={onChange} value={value} placeholder={`${entityName} ${t("address")}`} />
								</FormBlock>
							)}
						/>
					</div>
				</div>
				<DividerWithText vertical />
				<div className="flex flex-col gap-4 flex-1">
					<Text variants={TextVariantsEnum.H7}>{t("contact_info")}</Text>
					<div className="flex flex-col gap-4 mt-2">
						<Controller
							name="phone"
							control={control}
							rules={{
								validate: data => {
									const isValid = data ? phoneValidator(data) : true;
									return isValid || t("error:incorrect", { field: t("phone_number").toLowerCase() });
								}
							}}
							defaultValue={data.phone}
							render={({ field: { value, onChange } }) => (
								<FormBlock label={t("phone")} textVariant={TextVariantsEnum.Caption2} labelClassname="mb-2">
									<Input
										mask={"+___ ____ _____"}
										placeholder="+356 0000 0000"
										onChange={onChange}
										value={value}
										error={!!errors?.phone?.message}
										errorText={errors?.phone?.message}
										id="enter-phone-number"
									/>
								</FormBlock>
							)}
						/>
						<Controller
							name="website"
							control={control}
							rules={{
								validate: data => {
									const isValid = data ? data && validateURL(data) : true;
									return isValid || t("error:incorrect", { field: t("website") });
								}
							}}
							defaultValue={data.website}
							render={({ field: { value, onChange } }) => (
								<FormBlock label="Website" textVariant={TextVariantsEnum.Caption2} labelClassname="mb-2">
									<Input
										placeholder="www.yourwebsite.com"
										onChange={onChange}
										value={value}
										error={!!errors?.website?.message}
										errorText={errors?.website?.message}
										id="enter-website"
									/>
								</FormBlock>
							)}
						/>
						<Controller
							name="about"
							control={control}
							defaultValue={data.about}
							render={({ field: { value, onChange } }) => (
								<FormBlock label={t("about")} textVariant={TextVariantsEnum.Caption2} labelClassname="mb-2">
									<Input onChange={onChange} value={value} minRows={5} multipleRows={8} placeholder={t("about")} />
								</FormBlock>
							)}
						/>
					</div>
				</div>
				{!!thirdColumnContent && (
					<>
						<DividerWithText vertical />
						<div className="flex flex-col gap-4 flex-1">{thirdColumnContent}</div>
					</>
				)}
			</div>
		</Modal>
	);
};

export default EntitySettingsTemplateModal;
